// Reconnect.js
import React, { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  getDoc,
} from "firebase/firestore";

// === Helpers ===
function tsToMs(v) {
  if (!v) return null;
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (typeof v.seconds === "number") return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  return null;
}
function fmtDate(ms) {
  try { return ms ? new Date(ms).toLocaleString() : "Unknown"; }
  catch { return "Unknown"; }
}

// === Same “Quizmaster Bob” modal look & feel (matches csvquizedit.js) ===
function BobModal({
  open,
  onClose,
  title = "Quizmaster Bob says",
  message,
  confirmText = "OK",
  cancelText,
  onConfirm,
  imageSrc = "/media/Quizmasterbobpopup.png",
}) {
  if (!open) return null;
  const handleConfirm = () => { try { onConfirm && onConfirm(); } finally { onClose && onClose(); } };
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 2147483647,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(560px, 92vw)",
          background: "linear-gradient(180deg, #222a3a 0%, #1b2130 100%)",
          border: "1px solid rgba(255,255,255,0.2)",
          borderRadius: 16,
          boxShadow: "0 18px 60px rgba(0,0,0,0.5)",
          padding: "18px 18px 16px",
          color: "#eaf2ff",
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
            fontWeight: 900,
            fontSize: "1.2rem",
            color: "#ffe066",
            textShadow: "2px 2px 10px #222, 0 0 14px #09f",
            marginBottom: 6,
          }}
        >
          {title}
        </div>

        <img
          src={imageSrc}
          alt="Quizmaster Bob"
          style={{
            display: "block",
            width: 96,
            height: 96,
            objectFit: "cover",
            borderRadius: "50%",
            margin: "2px auto 10px",
            boxShadow: "0 6px 22px rgba(0,0,0,0.35)",
            border: "2px solid rgba(255,255,255,0.75)",
          }}
        />

        <div style={{ fontSize: "1.05rem", margin: "6px 4px 14px" }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          {cancelText && (
            <button
              onClick={onClose}
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                fontWeight: 800,
                border: "1px solid #888",
                borderRadius: 10,
                padding: "10px 20px",
                cursor: "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
              }}
            >
              {cancelText}
            </button>
          )}
          <button
            autoFocus
            onClick={handleConfirm}
            style={{
              background: "#47e373",
              color: "#111",
              fontWeight: 900,
              border: "none",
              borderRadius: 10,
              padding: "10px 22px",
              cursor: "pointer",
              boxShadow: "0 8px 22px rgba(0,0,0,0.35)",
            }}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

// Deep delete: answers + teams subcollections, then the game doc
async function deleteGameDeep(gameId) {
  // Delete 'answers'
  const ansSnap = await getDocs(collection(db, "games", gameId, "answers"));
  await Promise.all(ansSnap.docs.map(d => deleteDoc(doc(db, "games", gameId, "answers", d.id))));
  // Delete 'teams'
  const teamsSnap = await getDocs(collection(db, "games", gameId, "teams"));
  await Promise.all(teamsSnap.docs.map(d => deleteDoc(doc(db, "games", gameId, "teams", d.id))));
  // Finally delete the game doc
  await deleteDoc(doc(db, "games", gameId));
  return true;
}

/** Non-admin hosts may only reconnect within this window of game createdAt. */
export const RECONNECT_WINDOW_MS = 3 * 60 * 60 * 1000; // 3 hours

function isQwizardProfile(user, userProfile) {
  return (
    userProfile?.role === "Qwizard" ||
    user?.email === "rob@izeus.org" ||
    user?.email === "suelaw42@gmail.com"
  );
}

export default function Reconnect({ open, onClose, onReconnect }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState({ open: false, target: null, text: "" });
  const [errorAlert, setErrorAlert] = useState({ open: false, text: "" });
  const [working, setWorking] = useState(false);
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileReady, setProfileReady] = useState(false);

  // Load user and profile to check Qwizard status
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setProfileReady(false);
      if (u) {
        try {
          const snap = await getDoc(doc(db, "users", u.uid));
          setUserProfile(snap.exists() ? snap.data() : {});
        } catch {
          setUserProfile({});
        }
      } else {
        setUserProfile(null);
      }
      setProfileReady(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!open || !profileReady) return;
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError("");
        setSelectedId(null);

        const qwizard = isQwizardProfile(user, userProfile);
        const uid = user?.uid || null;
        const cutoffMs = Date.now() - RECONNECT_WINDOW_MS;

        const snap = await getDocs(collection(db, "games"));
        const base = snap.docs.map((d) => {
          const data = d.data() || {};
          const numQuestions = Array.isArray(data.questions)
            ? data.questions.length
            : (typeof data.qCount === "number" ? data.qCount : 0);
          return {
            id: d.id,
            title: data.title || data.name || data.quizName || d.id,
            joinCode: data.joinCode || data.code || "",
            numQuestions,
            current: Number.isFinite(data.current) ? Number(data.current) : 0,
            hostUid: data.hostUid || null,
            createdAtMs:
              tsToMs(data.createdAt) ||
              tsToMs(data.updatedAt) ||
              tsToMs(data.questionStartTimeServer) ||
              null,
            raw: { id: d.id, ...data },
          };
        }).filter((g) => {
          // Own quizzes only (Qwizard/admin sees all)
          if (!qwizard) {
            if (!uid || g.hostUid !== uid) return false;
            // 3-hour reconnect window for non-admins
            if (!g.createdAtMs || g.createdAtMs < cutoffMs) return false;
          }
          return true;
        });

        // team counts
        const withTeams = await Promise.all(
          base.map(async (g) => {
            try {
              const teamsSnap = await getDocs(collection(db, "games", g.id, "teams"));
              return { ...g, teamCount: teamsSnap.size || 0 };
            } catch {
              return { ...g, teamCount: 0 };
            }
          })
        );

        withTeams.sort((a, b) => (b.createdAtMs || 0) - (a.createdAtMs || 0));
        if (!cancelled) setRows(withTeams);
      } catch (e) {
        if (!cancelled) setError("Failed to load games.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [open, profileReady, user, userProfile]);

  if (!open) return null;

  const isQwizard = isQwizardProfile(user, userProfile);

  const attemptReconnect = (gameRaw) => {
    if (!onReconnect || !gameRaw) return;
    if (!isQwizard) {
      const createdMs =
        tsToMs(gameRaw.createdAt) ||
        tsToMs(gameRaw.updatedAt) ||
        tsToMs(gameRaw.questionStartTimeServer);
      if (!createdMs || Date.now() - createdMs > RECONNECT_WINDOW_MS) {
        setErrorAlert({
          open: true,
          text: "This quiz is older than 3 hours, so it can no longer be reconnected. Start a new live quiz if you need to host again.",
        });
        return;
      }
      if (user?.uid && gameRaw.hostUid && gameRaw.hostUid !== user.uid) {
        setErrorAlert({
          open: true,
          text: "You can only reconnect to quizzes you hosted.",
        });
        return;
      }
    }
    onReconnect(gameRaw);
  };

  const chosen = rows.find(r => r.id === selectedId) || null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "980px",
          maxWidth: "95vw",
          maxHeight: "85vh",
          background: "rgba(28,32,48,0.98)",
          borderRadius: 16,
          boxShadow: "0 18px 60px rgba(0,0,0,0.5)",
          overflow: "hidden",
          color: "#fff",
          fontFamily: "'Montserrat','Open Sans',Arial,sans-serif",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Title */}
        <div
          style={{
            padding: "14px 18px",
            fontWeight: 900,
            fontSize: "1.2rem",
            background: "rgba(255,255,255,0.06)",
          }}
        >
          Select a Quiz to reconnect to
          <div style={{ fontWeight: 600, fontSize: "0.85rem", opacity: 0.75, marginTop: 4 }}>
            {isQwizard
              ? "Qwizard: all quizzes, no time limit"
              : "Your quizzes only · reconnect within 3 hours of start"}
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: 14, overflow: "auto" }}>
          {loading || !profileReady ? (
            <div style={{ padding: 18, fontWeight: 800 }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 18, color: "#ff6b6b", fontWeight: 800 }}>{error}</div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 18, fontWeight: 800 }}>
              {isQwizard
                ? "No games found."
                : "No recent quizzes of yours to reconnect to (last 3 hours)."}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ textAlign: "left", fontSize: ".95rem", color: "#cfd3dc" }}>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff22" }}>Quiz Name</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff22" }}>Join code</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff22" }}>Questions</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff22" }}>Teams</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff22" }}>Current Q</th>
                  <th style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff22" }}>Quiz Date</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((g) => {
                  const isSel = selectedId === g.id;
                  return (
                    <tr
                      key={g.id}
                      onClick={() => setSelectedId(g.id)}
                      onDoubleClick={() => attemptReconnect(g.raw)}
                      style={{
                        cursor: "pointer",
                        background: isSel ? "rgba(255,255,255,0.12)" : "transparent",
                      }}
                    >
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff11" }}>{g.title}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff11" }}>{g.joinCode || "—"}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff11" }}>{g.numQuestions}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff11" }}>{g.teamCount}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff11" }}>{g.current}</td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #ffffff11" }}>{fmtDate(g.createdAtMs)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: 12,
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            background: "rgba(255,255,255,0.06)",
          }}
        >
          <button
            onClick={onClose}
            style={{
              background: "#555",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              padding: "8px 16px",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>

          <button
            onClick={() => {
              if (!chosen || !isQwizard) return;
              setConfirmDelete({
                open: true,
                target: chosen,
                text: `Are you sure you want to permanently delete "${chosen.title}" (Join Code: ${chosen.joinCode || "—"})? This removes the game, its teams, and answers.`,
              });
            }}
            disabled={!selectedId || working || !isQwizard}
            style={{
              background: selectedId && !working && isQwizard ? "#ff6b6b" : "#ff6b6b88",
              color: "#111",
              border: "none",
              borderRadius: 12,
              padding: "8px 16px",
              fontWeight: 900,
              cursor: selectedId && !working && isQwizard ? "pointer" : "not-allowed",
            }}
          >
            Delete
          </button>

          <button
            onClick={() => { if (chosen) attemptReconnect(chosen.raw); }}
            disabled={!selectedId || working}
            style={{
              background: selectedId && !working ? "#4ad174" : "#4ad17488",
              color: "#111",
              border: "none",
              borderRadius: 12,
              padding: "8px 16px",
              fontWeight: 900,
              cursor: selectedId && !working ? "pointer" : "not-allowed",
            }}
          >
            Reconnect
          </button>
        </div>
      </div>

      {/* Bob confirm for DELETE */}
      <BobModal
        open={confirmDelete.open}
        onClose={() => setConfirmDelete({ open: false, target: null, text: "" })}
        title="Quizmaster Bob"
        message={confirmDelete.text}
        cancelText="Cancel"
        confirmText="Yes, delete it"
        onConfirm={async () => {
          if (!confirmDelete.target) return;
          setWorking(true);
          try {
            await deleteGameDeep(confirmDelete.target.id);
            setRows((r) => r.filter((x) => x.id !== confirmDelete.target.id));
            setSelectedId(null);
          } catch (e) {
            setErrorAlert({ open: true, text: "Delete failed: " + (e?.message || String(e)) });
          } finally {
            setWorking(false);
          }
        }}
      />

      {/* Bob alert for errors only */}
      <BobModal
        open={errorAlert.open}
        onClose={() => setErrorAlert({ open: false, text: "" })}
        title="Quizmaster Bob"
        message={errorAlert.text}
        confirmText="OK"
      />
    </div>
  );
}
